import { useCallback, useMemo } from 'react';
import { SECTION_NAMES } from '@repo/common';

import { type CampaignTypes, PRIZE_POP_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns';
import { useEnablePrizePopCampaign, useEnableRebateCampaign, useEnableTournamentsCampaign } from 'store/featureFlags/featureFlagsStore';
import { useCampaigns } from 'features/campaigns/store';
import { useCampaignOnlyType } from 'store/app/appStore';

export const SECTION_TO_CAMPAIGN_TYPE: Partial<Record<string, CampaignTypes>> = {
  [SECTION_NAMES.TOURNAMENTS]: TOURNAMENTS_CAMPAIGN_TYPE,
  [SECTION_NAMES.PRIZE_POP]: PRIZE_POP_CAMPAIGN_TYPE,
  [SECTION_NAMES.REBATE]: REBATE_CAMPAIGN_TYPE,
};

// Inverse of SECTION_TO_CAMPAIGN_TYPE — the lobby section each campaign type lives in.
export const CAMPAIGN_TYPE_TO_SECTION = Object.fromEntries(
  Object.entries(SECTION_TO_CAMPAIGN_TYPE).map(([section, type]) => [type, section]),
) as Record<CampaignTypes, string>;

// Reads every campaign feature flag once and exposes a lookup by campaign
// type, so adding a new campaign type only means adding one entry here
// instead of another if-branch in each dispatcher component below.
export function useCampaignEnabledMap(): Partial<Record<CampaignTypes, boolean>> {
  return {
    [PRIZE_POP_CAMPAIGN_TYPE]: useEnablePrizePopCampaign(),
    [TOURNAMENTS_CAMPAIGN_TYPE]: useEnableTournamentsCampaign(),
    [REBATE_CAMPAIGN_TYPE]: useEnableRebateCampaign(),
  };
}

// Single subscription point for campaign visibility — combines feature flags
// and live campaign counts. Returns a tri-state:
// -  true  → campaign section, flag on, at least one campaign exists
// -  false → campaign section, but disabled or empty
// -  null  → not a campaign section (pass-through for non-campaign filters)
export function useCampaignSectionGate() {
  const enabledMap = useCampaignEnabledMap();
  const tournaments = useCampaigns(TOURNAMENTS_CAMPAIGN_TYPE);
  const prizePop = useCampaigns(PRIZE_POP_CAMPAIGN_TYPE);
  const rebate = useCampaigns(REBATE_CAMPAIGN_TYPE);
  // The in-game campaign-only view explicitly requests one campaign type up front.
  // While its data is still loading, campaignCountByType reads 0 exactly like a
  // genuinely empty campaign — this lets that case still resolve to the campaign
  // branch instead of falling back to a generic gameless Section for a frame.
  const campaignOnlyType = useCampaignOnlyType();

  const campaignCountByType = useMemo<Partial<Record<CampaignTypes, number>>>(
    () => ({
      [TOURNAMENTS_CAMPAIGN_TYPE]: tournaments.length,
      [PRIZE_POP_CAMPAIGN_TYPE]: prizePop.length,
      [REBATE_CAMPAIGN_TYPE]: rebate.length,
    }),
    [tournaments.length, prizePop.length, rebate.length],
  );

  const isCampaignEnabled = useCallback((sectionName: string): boolean | null => {
    const campaignType = SECTION_TO_CAMPAIGN_TYPE[sectionName];
    if (!campaignType) return null;
    if (!enabledMap[campaignType]) return false;
    // Requested campaign-only type bypasses the live count so a still-loading count of 0 isn't mistaken for empty.
    if (campaignOnlyType === campaignType) return true;
    return (campaignCountByType[campaignType] ?? 0) > 0;
  }, [enabledMap, campaignCountByType, campaignOnlyType]);

  return { isCampaignEnabled, campaignCountByType, enabledMap };
}
